import { forwardRef, useMemo, useRef } from "react";
import { useNavigate } from "react-router";
import { Box, CSSObject } from "@mui/material";
import {
  CellApiModule,
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  CsvExportModule,
  CustomEditorModule,
  DateFilterModule,
  DisplayedColumnsChangedEvent,
  EventApiModule,
  GridApi,
  GridOptions,
  GridReadyEvent,
  GridState,
  GridStateModule,
  ModuleRegistry,
  NumberFilterModule,
  PaginationChangedEvent,
  PaginationModule,
  PinnedRowModule,
  QuickFilterModule,
  RenderApiModule,
  RowApiModule,
  RowAutoHeightModule,
  RowDragModule,
  RowSelectionModule,
  RowStyleModule,
  ScrollApiModule,
  setupAgTestIds,
  SortChangedEvent,
  TextEditorModule,
  TextFilterModule,
  ValidationModule,
} from "ag-grid-community";
import {
  AdvancedFilterModule,
  CellSelectionModule,
  ClipboardModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ExcelExportModule,
  FiltersToolPanelModule,
  MenuModule,
  RichSelectModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  SetFilterModule,
  TreeDataModule,
} from "ag-grid-enterprise";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import { useDebouncedCallback } from "use-debounce";

import { getUserPreferenceValue, useUpdateUserPreferencesMutation } from "fond/api";
import { PAGINATION_KEY } from "fond/constants";
import { UserPreferenceKey } from "fond/types";
import { GridId } from "fond/types/grids";
import { areWeTestingWithJest } from "fond/utils";
import { useAppSelector, useQueryParams } from "fond/utils/hooks";

import BlockSpinner from "../BlockSpinner";

import { buildTheme } from "./theme/themes";

// import "ag-grid-community/styles/ag-grid.css";
import "./theme/ag-base.scss";

if (import.meta.env.DEV) {
  ModuleRegistry.registerModules([ValidationModule]);
  // Setup test IDs for all instances of AG Grid that are created after this call.
  setupAgTestIds();
}

ModuleRegistry.registerModules([
  AdvancedFilterModule,
  CellApiModule,
  CellStyleModule,
  CellSelectionModule,
  ClientSideRowModelModule,
  ClientSideRowModelApiModule,
  ClipboardModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  CsvExportModule,
  CustomEditorModule,
  DateFilterModule,
  ExcelExportModule,
  EventApiModule,
  FiltersToolPanelModule,
  MenuModule,
  NumberFilterModule,
  PaginationModule,
  PinnedRowModule,
  QuickFilterModule,
  RenderApiModule,
  RichSelectModule,
  RowApiModule,
  RowAutoHeightModule,
  RowDragModule,
  RowSelectionModule,
  RowStyleModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  ScrollApiModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  SetFilterModule,
  TextEditorModule,
  TextFilterModule,
  TreeDataModule,
  GridStateModule,
]);

export interface FONDAgGridProps extends AgGridReactProps {
  /**
   * The unique identifier of the grid. This is used to save the grid state and allow
   * the users customised state to be restored on re-rendering the grid.
   */
  id?: GridId;
  /**
   * The grid works out the best width for columns
   * @default true
   */
  containerProps?: CSSObject;
  gridOptions?: GridOptions;
  variant?: "borderless" | "outlined";
  size?: "compact" | "standard" | "large";
  /**
   * Whether to persist the current pagination page in the URL query string.
   */
  persistentPagination?: boolean;
}

type ColumnState = Pick<GridState, "columnOrder" | "columnPinning" | "columnSizing" | "columnVisibility" | "pagination" | "sort">;
type ColumnStateEvent = DisplayedColumnsChangedEvent | ColumnResizedEvent | ColumnPinnedEvent | PaginationChangedEvent | SortChangedEvent;
const LoadingOverlay = () => <BlockSpinner />;

const AgGrid = forwardRef<AgGridReact, FONDAgGridProps>(
  (
    {
      id,
      containerProps = { height: "100%", width: "100%" },
      gridOptions,
      onGridReady,
      variant = "borderless",
      size = "standard",
      persistentPagination = false,
      ...gridProps
    }: FONDAgGridProps,
    ref
  ) => {
    const [updatePreference] = useUpdateUserPreferencesMutation();
    const gridApi = useRef<GridApi | null>();
    const wrapperRef = useRef<HTMLElement | null>(null);
    const pageParam = useQueryParams(PAGINATION_KEY);
    const navigate = useNavigate();

    const defaultOptions: GridOptions = useMemo(
      () => ({
        theme: buildTheme(size, variant),
        columnMenu: "legacy",
        // Sets default column values (These can be set individually in column definitions)
        defaultColDef: {
          sortable: true,
          resizable: true,
          filter: true,
          enableRowGroup: true,
        },
        sideBar: {
          toolPanels: [
            {
              id: "filters",
              labelDefault: "Filters",
              labelKey: "filters",
              iconKey: "filter",
              toolPanel: "agFiltersToolPanel",
              toolPanelParams: {
                suppressExpandAll: true,
                suppressFilterSearch: true,
              },
            },
          ],
        },
        popupParent: document.querySelector("body") || undefined,
        reactiveCustomComponents: true,
        suppressContextMenu: false,
        suppressCellFocus: true,
        suppressDragLeaveHidesColumns: true,
        suppressMenuHide: false,
        rowGroupPanelShow: "always",
        // AgGrid currently does not remove the loadingOverlay when running jest tests
        // if the data loaded is an empty array (indicating it should show the empty rows component)
        loadingOverlayComponent: areWeTestingWithJest() ? undefined : LoadingOverlay,
      }),
      [size, variant]
    );

    /**
     * The grid has initialised
     */
    const handleOnGridReady = (event: GridReadyEvent) => {
      gridApi.current = event.api;

      // To support popups within floating windows we need to set the popup parent
      event.api.setGridOption("popupParent", handleGetDocument().body);

      // ensure that both the passed in gridOptions and onGridReady are called
      gridOptions?.onGridReady?.(event);
      onGridReady?.(event);

      // If an id has been supplied we set the grid events that modified state we are maintaining.
      if (id) {
        event.api.setGridOption("onDisplayedColumnsChanged", handleOnDisplayedColumnsChanged);
        event.api.setGridOption("onColumnPinned", handleStateUpdate);
        event.api.setGridOption("onSortChanged", handleStateUpdate);
        event.api.setGridOption("onColumnResized", handleOnColumnResize);
        event.api.setGridOption("onPaginationChanged", handleOnPageSizeChanged);
      }

      // if we want to persist pagination we set an update handler to save the current page to the URL
      // and also attempt to restore to that the page on load.
      if (persistentPagination) {
        // if a pageParam exists on load we navigate to that page
        if (pageParam) {
          event.api?.paginationGoToPage(Number(pageParam) - 1 || 0);
        }

        const oldPaginationChangeHandler = event.api.getGridOption("onPaginationChanged");

        event.api.setGridOption("onPaginationChanged", (e) => {
          if (persistentPagination === true) {
            // use URLSearchParams to preserve other query string params
            const search = new URLSearchParams(window.location.search);
            search.set(PAGINATION_KEY, String(e.api.paginationGetCurrentPage() + 1));
            navigate(`?${search.toString()}`, { replace: true });
            oldPaginationChangeHandler?.(e);
          }
        });
      }
    };

    const options = { ...defaultOptions, ...gridOptions };

    /**
     * Allows overriding what document is used. Currently used by Drag and Drop.
     * We need to specify this to support loading the grid inside a floating window.
     */
    const handleGetDocument = (): Document => {
      return wrapperRef.current?.ownerDocument || window.document;
    };

    /**
     * Saves column state changes to user preferences to allow for the grid
     * state to be restore when loaded next time.
     */
    const handleStateUpdate = useDebouncedCallback((event: ColumnStateEvent) => {
      if (id) {
        const { columnOrder, columnPinning, columnSizing, columnVisibility, sort, pagination } = event.api.getState();

        updatePreference({
          Key: UserPreferenceKey.GRID_STATE,
          Subkey: id,
          Value: {
            columnOrder,
            columnPinning,
            columnSizing,
            columnVisibility,
            sort,
            pagination: { pageSize: pagination?.pageSize },
          },
        });
      }
    }, 1000);

    const handleOnDisplayedColumnsChanged = (event: DisplayedColumnsChangedEvent) => {
      if (id && event.source !== "gridInitializing") handleStateUpdate(event);
    };

    const handleOnColumnResize = (event: ColumnResizedEvent) => {
      if (id && event.finished) handleStateUpdate(event);
    };

    const handleOnPageSizeChanged = (event: PaginationChangedEvent) => {
      if (id && event.newPageSize) handleStateUpdate(event);
    };

    /**
     * The initialState allows the grid to be restored to a previous state. Note that we only use the initialState
     * if the grid has an id set.
     *
     * For example:
     * - Column visibility
     * - Column order
     * - Pinned Columns
     *
     * The handleStateUpdate() above determines the partial GridState we save & can therefore restore.
     */
    const initialState = useAppSelector((state) => (id ? getUserPreferenceValue(state, UserPreferenceKey.GRID_STATE, id) : undefined));

    return (
      <Box
        sx={containerProps}
        className={`ag-theme-${size} ${variant} ${options.domLayout === "autoHeight" ? "auto-height" : ""}`}
        data-testid="ag-grid"
        ref={wrapperRef}
      >
        <AgGridReact
          gridId={id}
          key={id}
          ref={ref}
          {...gridProps}
          initialState={initialState}
          gridOptions={options}
          onGridReady={handleOnGridReady}
          getDocument={handleGetDocument}
        />
      </Box>
    );
  }
);

AgGrid.displayName = "AgGrid";
export default AgGrid;
